import { logComponentError } from '@ms/yammer-diagnostics-state';
import type { ErrorCode } from '@ms/yammer-telemetry';
import { reportError } from '@ms/yammer-telemetry';

import type { EcsProjectTeamName } from './ecsConfig';
import type { Experiment, ExperimentTreatmentValue, ExperimentTreatments } from './experiments';
import { experimentConfigs } from './experiments';
import type { FeatureRolloutTreatments } from './featureRollouts';
import { featureRolloutNames } from './featureRollouts';
import type { EcsServiceSnapshot } from './store';

type SectionTreatments = Readonly<Record<string, ExperimentTreatmentValue>>;

export interface EcsPayload {
  readonly Headers: {
    readonly ETag: string;
  };
  readonly ConfigIDs: Readonly<Record<string, string>>;
  readonly YammerClients: SectionTreatments | undefined;
  readonly CrossPlatform: SectionTreatments | undefined;
  readonly ClientForward: SectionTreatments | undefined;
}

const reportExperimentValidationError = (error: Error, errorCode: ErrorCode): void => {
  logComponentError('Experiments', error);
  reportError({ error, eventProperties: { errorCode } });
};

const isExperiment = (experiment: string): experiment is Experiment =>
  Object.prototype.hasOwnProperty.call(experimentConfigs, experiment);

const isValidExperimentTreatment = (
  experiment: string,
  treatmentValue: ExperimentTreatmentValue,
  projectTeam: EcsProjectTeamName
): experiment is Experiment => {
  if (!isExperiment(experiment)) {
    return false;
  }

  const config = experimentConfigs[experiment];

  let isValid = true;

  if (config.projectTeam !== projectTeam) {
    reportExperimentValidationError(
      new Error(
        `Experiments validation failed - experiment "${experiment}" was received under projectTeam "${projectTeam}" but is configured for projectTeam "${config.projectTeam}"`
      ),
      'EcsExperimentProjectTeamMismatch'
    );
    isValid = false;
  }

  if (!config.treatments.some((validTreatment) => validTreatment === treatmentValue)) {
    reportExperimentValidationError(
      new Error(
        `Experiments validation failed - experiment "${experiment}" received treatment "${treatmentValue}" which is not one of its configured treatments: ${config.treatments.join(
          ', '
        )}`
      ),
      'EcsExperimentInvalidTreatment'
    );
    isValid = false;
  }

  return isValid;
};

const pickValidatedExperimentTreatments = (
  treatments: SectionTreatments | undefined,
  projectTeam: EcsProjectTeamName
): Partial<ExperimentTreatments> => {
  if (!treatments) {
    return {};
  }

  return Object.entries(treatments).reduce<Partial<ExperimentTreatments>>(
    (validatedTreatments, [name, value]) =>
      isValidExperimentTreatment(name, value, projectTeam)
        ? {
            ...validatedTreatments,
            [name]: value,
          }
        : validatedTreatments,
    {}
  );
};

const pickFeatureRolloutTreatments = (treatments: SectionTreatments | undefined): Partial<FeatureRolloutTreatments> => {
  if (!treatments) {
    return {};
  }

  return featureRolloutNames.reduce<Partial<FeatureRolloutTreatments>>((featureRolloutTreatments, featureRollout) => {
    if (Object.prototype.hasOwnProperty.call(treatments, featureRollout)) {
      Reflect.set(featureRolloutTreatments, featureRollout, treatments[featureRollout]);
    }

    return featureRolloutTreatments;
  }, {});
};

export const normalizeEcsPayload = ({
  ConfigIDs: configIds,
  Headers: { ETag: etag },
  YammerClients: yammerClientsTreatments,
  CrossPlatform: crossPlatformTreatments,
  ClientForward: clientForwardTreatments,
}: EcsPayload): EcsServiceSnapshot => ({
  ecsConfig: { configIds, etag },
  experimentTreatmentsFromService: {
    ...pickValidatedExperimentTreatments(yammerClientsTreatments, 'YammerClients'),
    ...pickValidatedExperimentTreatments(crossPlatformTreatments, 'CrossPlatform'),
    ...pickValidatedExperimentTreatments(clientForwardTreatments, 'ClientForward'),
  },
  featureRolloutTreatmentsFromService: pickFeatureRolloutTreatments(yammerClientsTreatments),
});
